import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';

import { functionsUsEast1 } from '@lib/firebase';
import {
  getUserBookings,
  getBooking,
  checkBikeAvailability,
} from '@lib/firebase/bookings';

/**
 * Hook to get user's bookings
 */
export function useUserBookings() {
  return useQuery({
    queryKey: ['bookings', 'user'],
    queryFn: async () => {
      // This will need to be updated to get current user ID
      // For now, we'll need to pass userId
      throw new Error('useUserBookings requires userId - use useUserBookingsById instead');
    },
  });
}

/**
 * Hook to get user's bookings by user ID
 */
export function useUserBookingsById(userId: string) {
  return useQuery({
    queryKey: ['bookings', 'user', userId],
    queryFn: () => getUserBookings(userId),
    enabled: !!userId,
  });
}

/**
 * Hook to get a single booking
 */
export function useBooking(bookingId: string | null) {
  return useQuery({
    queryKey: ['booking', bookingId],
    queryFn: () => {
      if (!bookingId) throw new Error('Booking ID required');
      return getBooking(bookingId);
    },
    enabled: !!bookingId,
  });
}

/**
 * Hook to check bike availability
 */
export function useBikeAvailability(
  bikeId: string | null,
  startTime: Date | null,
  endTime: Date | null,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['bike-availability', bikeId, startTime?.getTime(), endTime?.getTime()],
    queryFn: async () => {
      if (!bikeId || !startTime || !endTime) {
        throw new Error('Bike ID, start time, and end time are required');
      }
      return checkBikeAvailability(bikeId, startTime, endTime);
    },
    enabled: enabled && !!bikeId && !!startTime && !!endTime,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to create a booking
 */
export function useCreateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      bikeId: string;
      locationId: string;
      startTime: Date;
      endTime: Date;
      rentalType: 'half-day' | 'full-day' | 'multi-day';
      numberOfDays?: number;
      halfDayRate: number;
      fullDayRate: number;
      appliedDiscount?: number;
      totalPrice: number;
      insuranceIncluded: boolean;
      insurancePrice?: number;
      insuranceProvider?: 'oyster' | 'other';
      insurancePolicyId?: string;
      insuranceQuoteId?: string;
      emergencyContactName?: string;
      emergencyContactPhone?: string;
    }): Promise<{ bookingId: string }> => {
      // Use Cloud Function for server-side validation and availability check
      const createBookingFn = httpsCallable<
        typeof data,
        { bookingId: string; success: boolean }
      >(functionsUsEast1, 'createBooking');

      const result = await createBookingFn(data);
      return { bookingId: result.data.bookingId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

/**
 * Hook to cancel a booking
 */
export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { bookingId: string; reason?: string }): Promise<void> => {
      const cancelBookingFn = httpsCallable<
        { bookingId: string; reason?: string },
        { success: boolean }
      >(functionsUsEast1, 'cancelBooking');

      await cancelBookingFn(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

/**
 * Hook to confirm booking (after payment)
 */
export function useConfirmBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      bookingId: string;
      paymentId: string;
    }): Promise<void> => {
      const confirmBookingFn = httpsCallable<
        typeof data,
        { success: boolean }
      >(functionsUsEast1, 'confirmBooking');

      await confirmBookingFn(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

/**
 * Hook to check availability via Cloud Function
 */
export function useCheckAvailability() {
  return useMutation({
    mutationFn: async (data: {
      bikeId: string;
      startTime: Date;
      endTime: Date;
      excludeBookingId?: string;
    }): Promise<{ available: boolean; conflicts?: Array<{ id: string; startTime: Date; endTime: Date }> }> => {
      const checkAvailabilityFn = httpsCallable<
        {
          bikeId: string;
          startTime: string;
          endTime: string;
          excludeBookingId?: string;
        },
        { available: boolean; conflicts?: Array<{ id: string; startTime: string; endTime: string }> }
      >(functionsUsEast1, 'checkAvailability');

      const result = await checkAvailabilityFn({
        ...data,
        startTime: data.startTime.toISOString(),
        endTime: data.endTime.toISOString(),
      });

      return {
        available: result.data.available,
        conflicts: result.data.conflicts?.map((c) => ({
          id: c.id,
          startTime: new Date(c.startTime),
          endTime: new Date(c.endTime),
        })),
      };
    },
  });
}

